import * as Dialog from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import { ClassValue } from 'clsx';
import { useTranslation } from 'react-i18next';
import cn from '../../app/utils/cn';
import { IconButton } from './IconButton';

type ModalVariant = 'center' | 'sheet';

interface IModalProps {
  children: React.ReactNode;
  className?: ClassValue;
  title?: string;
  titlePosition?: 'start' | 'center';
  variant?: ModalVariant;
  onClose?: () => void;
}

const variantClass: Record<ModalVariant, string> = {
  center:
    'top-1/2 left-1/2 mx-4 w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-xl',
  sheet:
    'bottom-0 left-1/2 w-full max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-t-2xl sm:top-1/2 sm:bottom-auto sm:w-[calc(100vw-2rem)] sm:-translate-y-1/2 sm:rounded-xl',
};

export default function Modal({
  children,
  className,
  title,
  titlePosition = 'start',
  variant = 'center',
  onClose,
}: IModalProps) {
  const { t } = useTranslation();

  return (
    <Dialog.Root
      open={true}
      onOpenChange={(open) => {
        if (!open) onClose?.();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-10 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'bg-surface-modal text-text-primary fixed z-20 p-4 shadow',
            variantClass[variant],
            className,
          )}
        >
          <header
            className={cn(
              'relative flex shrink-0 items-center rounded-t-xl py-2',
              titlePosition === 'center' && 'justify-center',
            )}
          >
            {title ? (
              <Dialog.Title className="text-xl">{title}</Dialog.Title>
            ) : (
              <Dialog.Title className="sr-only">
                {t('sidenavbar.actions.modal', { defaultValue: 'Modal' })}
              </Dialog.Title>
            )}

            {!!onClose && (
              <Dialog.Close asChild>
                <IconButton
                  size="lg"
                  aria-label={t('sidenavbar.actions.closeModal')}
                  className="absolute top-0 right-0"
                >
                  <Cross2Icon className="h-6 w-6" />
                </IconButton>
              </Dialog.Close>
            )}
          </header>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
